import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Actions,
  BrandHeaderRow,
  BrandLogo,
  Card,
  Header,
  HeaderIconButton,
  HeaderText,
  HomeIcon,
  Page,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  Tooltip,
  Legend
);

const ChartCard = styled(Card)`
  canvas {
    background: rgba(248, 250, 252, 0.96);
    border-radius: 22px;
    padding: 1rem;
  }
`;

const HeaderActions = styled(Actions)`
  align-self: flex-start;
  margin-left: auto;
`;

const AlarmVisualization = () => {
  const [alarms, setAlarms] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAlarms = async () => {
      try {
        const response = await axios.get(`${window.RUNTIME_CONFIG.apiUrl}/alarms`, {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        });
        console.log('Fetched alarms:', response.data);
        setAlarms(response.data);
      } catch (error) {
        console.error('Failed to fetch alarms:', error);
      }
    };

    fetchAlarms();
  }, [user]);

  // Process alarms data for visualization
  const processData = () => {
    console.log('Processing alarms:', alarms);

    if (!alarms || alarms.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label: 'Alarm Times',
            data: [],
            borderColor: 'rgba(53, 162, 235, 1)',
            backgroundColor: 'rgba(53, 162, 235, 0.5)',
            tension: 0.4
          },
          {
            label: 'Creation Times',  // Add new empty dataset
            data: [],
            borderColor: 'rgba(153, 102, 255, 1)',
            backgroundColor: 'rgba(153, 102, 255, 0.5)',
            tension: 0.4
          }
        ]
      };
    }

    // Sort alarms by createdAt
    const sortedAlarms = [...alarms].sort((a, b) => 
      new Date(a.createdAt) - new Date(b.createdAt)
    );

    // Create arrays for labels and data
    const labels = [];
    const timeData = [];
    const creationTimeData = [];  // New array for creation times
    const successPoints = [];
    const failedPoints = [];

    sortedAlarms.forEach((alarm) => {
      try {
        const date = new Date(alarm.createdAt);
        const time = new Date(alarm.time);
        const creationHours = date.getHours() + date.getMinutes() / 60;  // Calculate creation time
        const hours = time.getHours() + time.getMinutes() / 60;
        const label = date.toLocaleDateString();
        
        labels.push(label);
        timeData.push(hours);
        creationTimeData.push(creationHours);  // Add creation time data
        
        // Add points for the same position
        if (alarm.failed) {
          successPoints.push(null);
          failedPoints.push(hours);
        } else {
          successPoints.push(hours);
          failedPoints.push(null);
        }
      } catch (error) {
        console.error('Error processing alarm:', alarm, error);
      }
    });

    return {
      labels,
      datasets: [
        {
          label: 'Alarm Times',
          data: timeData,
          borderColor: 'rgba(53, 162, 235, 1)',
          backgroundColor: 'rgba(53, 162, 235, 0.5)',
          tension: 0.4,
          pointRadius: 4
        },
        {
          label: 'Creation Times',  // New dataset
          data: creationTimeData,
          borderColor: 'rgba(153, 102, 255, 1)',
          backgroundColor: 'rgba(153, 102, 255, 0.5)',
          tension: 0.4,
          pointRadius: 4
        },
        {
          label: 'Successful',
          data: successPoints,
          borderColor: 'rgba(75, 192, 75, 1)',
          backgroundColor: 'rgba(75, 192, 75, 1)',
          pointRadius: 6,
          showLine: false
        },
        {
          label: 'Failed',
          data: failedPoints,
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 1)',
          pointRadius: 6,
          showLine: false
        }
      ]
    };
  };

  const options = {
    responsive: true,
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Date'
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Time of Day'
        },
        min: 0,
        max: 24,
        ticks: {
          stepSize: 1,
          callback: (value) => {
            const hours = Math.floor(value);
            const minutes = Math.round((value - hours) * 60);
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          }
        }
      }
    },
    plugins: {
      title: {
        display: true,
        text: 'Alarm Times Over Last 30 Days'
      },
      legend: {
        position: 'top',
      },
      tooltip: {
        filter: (tooltipItem) => {
          // Only show tooltip for Success and Failed points
          return tooltipItem.dataset.label !== 'Alarm Times';
        },
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            if (value === null) return null;
            
            const hours = Math.floor(value);
            const minutes = Math.round((value - hours) * 60);
            const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            
            if (context.dataset.label === 'Successful') {
              return `Alarm Time: ${timeStr} (Success)`;
            } else if (context.dataset.label === 'Failed') {
              return `Alarm Time: ${timeStr} (Failed)`;
            } else if (context.dataset.label === 'Creation Times') {
              return `Creation Time: ${timeStr}`;
            }
          }
        }
      }
    }
  };

  return (
    <Page>
      <Shell $wide>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderActions>
                <HeaderIconButton type="button" aria-label="Home" onClick={() => navigate('/dashboard')}>
                  <HomeIcon aria-hidden="true" />
                </HeaderIconButton>
              </HeaderActions>
            </BrandHeaderRow>
            <Title>Pledge chart.</Title>
            <Subtitle>See when pledges were created, when they were due, and how they turned out.</Subtitle>
          </HeaderText>
        </Header>
        <ChartCard>
          <Line options={options} data={processData()} />
        </ChartCard>
      </Shell>
    </Page>
  );
};

export default AlarmVisualization;
